import { AxiosError } from 'axios'
import { isArray } from 'lodash'
import { action, computed, flow, makeAutoObservable, observable } from 'mobx'

import { requestWrapper } from 'Shared/utils'
import { deleteAllSearchParams } from 'Shared/utils/url/helpers'

import { EMPTY_LABELVALUE, STORE_UI } from 'Core/constants'
import { CustomGenerator } from 'Core/interfaces'
import {
	entityToStringLabelValue,
	getDefaultStoreArrayValue,
	getDefaultStoreValue
} from 'Core/utils'

import { STORE_KPI, STORE_LOCATION_BY_PERIODS } from 'Portals/Customer/constants'
import { KpiFilterFields, Location, LocationFilterFields, TagType } from 'Portals/Customer/entities'
import { LocationRepo } from 'Portals/Customer/repositories'
import { RootStore } from 'Portals/Customer/stores'
import { formatKpiLocationFilterOptions } from 'Portals/Customer/utils/dataFormat'

import { LocationTable } from './LocationTable'

export class LocationStore {
	table: LocationTable
	@observable region = getDefaultStoreArrayValue<Entity>()
	@observable division = getDefaultStoreArrayValue<Entity>()
	@observable businessUnit = getDefaultStoreArrayValue<Entity>()
	@observable filterLoading: boolean = false
	@observable selectedLocations = getDefaultStoreValue<number>()
	@observable isMobileFilterOpen: boolean = false
	@observable kpiFilterOptions = getDefaultStoreArrayValue<Location.FilterOptions>()

	constructor(public rootStore: RootStore) {
		this.table = new LocationTable(rootStore)
		makeAutoObservable(this, {}, { autoBind: true })
	}

	@computed
	get regionOptions() {
		const result = this.region.value.length
			? [EMPTY_LABELVALUE, ...this.region.value.map(entityToStringLabelValue)]
			: []
		return result
	}

	@computed
	get divisionOptions() {
		const result = this.division.value.length
			? [EMPTY_LABELVALUE, ...this.division.value.map(entityToStringLabelValue)]
			: []
		return result
	}

	@computed
	get businessUnitOptions() {
		const result = this.businessUnit.value.length
			? [EMPTY_LABELVALUE, ...this.businessUnit.value.map(entityToStringLabelValue)]
			: []
		return result
	}

	@computed
	get kpiLocationOptions() {
		return formatKpiLocationFilterOptions(this.kpiFilterOptions.value)
	}

	@computed
	get kpiLocationLength() {
		return this.kpiFilterOptions.value.length
	}

	@computed
	get kpiCategoryOptions() {
		const selectedserviceSiteId =
			this.rootStore[STORE_KPI].table.filters[KpiFilterFields.serviceSiteId]?.data

		const correctValue = isArray(selectedserviceSiteId)
			? selectedserviceSiteId[0]
			: selectedserviceSiteId

		return (
			this.kpiLocationOptions.find((opt) => opt.value === correctValue)?.categoriesOptions ||
			[]
		)
	}

	@action
	clearRegionFilter() {
		this.region = getDefaultStoreArrayValue()
		delete this.table.filters.region
	}

	@action
	clearDivisionFilter() {
		this.division = getDefaultStoreArrayValue()
		delete this.table.filters.division
	}

	@action
	clearbusinessUnitFilter() {
		this.businessUnit = getDefaultStoreArrayValue()
		delete this.table.filters.businessUnit
	}

	@flow
	*retrieveFilters(filter: LocationFilterFields): CustomGenerator<Entity[]> {
		this.filterLoading = true
		try {
			switch (filter) {
				case LocationFilterFields.divisionId: {
					this.division = getDefaultStoreArrayValue()
					this.division.value = yield LocationRepo.retrieveFilters({
						divisionId: null,
						regionId: (this.table.filters?.regionId?.data as BasicString) || null,
						businessUnitId:
							(this.table.filters?.businessUnitId?.data as BasicString) || null,
						tagType: TagType.division
					})
					break
				}

				case LocationFilterFields.regionId: {
					this.region = getDefaultStoreArrayValue()
					this.region.value = yield LocationRepo.retrieveFilters({
						divisionId: (this.table.filters?.divisionId?.data as BasicString) || null,
						regionId: null,
						businessUnitId:
							(this.table.filters?.businessUnitId?.data as BasicString) || null,
						tagType: TagType.region
					})
					break
				}

				case LocationFilterFields.businessUnitId: {
					this.businessUnit = getDefaultStoreArrayValue()
					this.businessUnit.value = yield LocationRepo.retrieveFilters({
						divisionId: (this.table.filters?.divisionId?.data as BasicString) || null,
						regionId: (this.table.filters?.regionId?.data as BasicString) || null,
						businessUnitId: null,
						tagType: TagType.businessUnit
					})
					break
				}
			}
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.filterLoading = false
		}
	}

	@flow
	*addLocation(payload: Location.Request.Add) {
		try {
			yield LocationRepo.addLocation(payload)
			this.retrieveSelectedLocations()
			this.rootStore[STORE_LOCATION_BY_PERIODS].table.retrieveLocationOptions()
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
			throw error
		}
	}

	@flow
	*retrieveSelectedLocations(): CustomGenerator<number> {
		yield requestWrapper({
			field: this.selectedLocations,
			repository: LocationRepo.retrieveSelectedLocations,
			repositoryParams: null
		})
	}

	@action
	clearLocation(cb?: () => void) {
		this.clearLocationFilters()
		this.clearLocationData()
		this.isMobileFilterOpen = false
		deleteAllSearchParams()
		cb && cb()
	}

	@action
	clearLocationFilters() {
		this.table.clearFilters()
		this.table.clearSorting()
		this.division = getDefaultStoreArrayValue()
		this.region = getDefaultStoreArrayValue()
		this.businessUnit = getDefaultStoreArrayValue()
		this.table.clearSearchFilters()
	}

	@action
	clearLocationData() {
		this.table.items = []
		this.table.totalCount = 0
	}

	@action
	onChangeFilterPanel(open: boolean) {
		this.isMobileFilterOpen = open
	}

	@flow
	*retrieveKpiFilterOptions() {
		yield requestWrapper({
			field: this.kpiFilterOptions,
			repository: LocationRepo.retrieveKpiFilterOptions,
			repositoryParams: null
		})
	}
}
